<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { useUsersStore } from '../stores/users.js'
import { useOrgStore } from '../stores/orgStore.js'
import { useSubOrgStore } from '../stores/subOrgStore.js'
import AdminActionDialog from '../components/AdminActionDialog.vue'
import { addUserSubOrgRequest, removeUserSubOrgRequest } from '../services/userService.js'
import { getAssignableRoles } from '../utils/permissions.js'
import { hasSubOrgAccess } from '../utils/subOrgAccess.js'
import { awaitScopedContextReady } from '../utils/contextReady.js'
import { getAdminEmptyStateMessage, parseAdminError } from '../utils/adminUi.js'
import { canModifyOrg, canModifySubOrg } from '../utils/lifecycleUtils.js'
import { useDebouncedValue } from '../utils/debounce.js'
import { DEFAULT_ADMIN_PAGE_SIZE, DEFAULT_ADMIN_SEARCH_DEBOUNCE_MS, paginateRows, toPositiveInt } from '../utils/operationsUi.js'
import { useQueryState } from '../composables/useQueryState.js'
import UserTable from '../components/UserTable.vue'
import UserSkeletonCard from '../components/UserSkeletonCard.vue'
import CreateUserForm from '../components/CreateUserForm.vue'
import UsersFilterBar from '../components/UsersFilterBar.vue'
import UsersSummaryBar from '../components/UsersSummaryBar.vue'
import UsersLifecycleWarnings from '../components/UsersLifecycleWarnings.vue'

const authStore = useAuthStore()
const usersStore = useUsersStore()
const orgStore = useOrgStore()
const subOrgStore = useSubOrgStore()
const route = useRoute()
const router = useRouter()
const selectedUsers = ref(new Set())
const selectedBulkSubOrgId = ref('')
const assignLoading = ref(false)
const operationError = ref('')
const operationMessage = ref('')
const subOrgModalOpen = ref(false)
const selectedUserForSubOrg = ref(null)
const subOrgModalLoading = ref(false)
const subOrgModalSaving = ref(false)
const subOrgModalError = ref('')
const selectedRole = ref('')
const originalRole = ref('')
const selectedSubOrgs = ref(new Set())
const originalSubOrgs = ref(new Set())
const usersList = computed(() => usersStore.users ?? [])
const highlightSubOrgId = computed(() => {
  const value = Number(selectedBulkSubOrgId.value)
  return Number.isInteger(value) && value > 0 ? value : null
})
const scopedSubOrgs = computed(() => subOrgStore.byOrg(orgStore.activeOrgId))
const assignableRoles = computed(() => getAssignableRoles(orgStore.activeOrgRole))
const roleAssignmentDisabled = computed(() => {
  return subOrgModalSaving.value
    || assignableRoles.value.length === 0
    || getUserDeletedMembershipCount(selectedUserForSubOrg.value) > 0
})
const lastLoadedContextKey = ref('')
const deletingUserIds = ref(new Set())
const bulkDeleteLoading = ref(false)
const pendingAction = ref(null)
const pendingActionLoading = ref(false)
const hasSelectedUsers = computed(() => selectedUsers.value.size > 0)
const bulkActionLocked = computed(() => assignLoading.value || bulkDeleteLoading.value)
const updatingUserIds = ref(new Set())
const userSearch = ref('')
const debouncedUserSearch = useDebouncedValue(userSearch, DEFAULT_ADMIN_SEARCH_DEBOUNCE_MS)
const selectedRoleFilter = ref('all')
const selectedOrgFilter = ref('all')
const selectedSubOrgFilter = ref('all')
const selectedLifecycleFilter = ref('all')
const listPage = ref(1)
const listPageSize = ref(DEFAULT_ADMIN_PAGE_SIZE)
const hasPendingRoleChange = computed(() => {
  return Boolean(selectedRole.value) && selectedRole.value !== originalRole.value
})
const emptyUsersMessage = computed(() => getAdminEmptyStateMessage('users'))
const activeOrg = computed(() => {
  return orgStore.organizations.find((org) => Number(org?.id) === Number(orgStore.activeOrgId)) ?? null
})
const activeSubOrg = computed(() => {
  return (orgStore.subOrgs ?? []).find((subOrg) => Number(subOrg?.id) === Number(subOrgStore.currentSubOrgId)) ?? null
})
const contextReadOnlyMessage = computed(() => {
  if (activeOrg.value && !canModifyOrg(activeOrg.value)) {
    return 'Selected organization is deleted. User and membership actions are read-only.'
  }

  if (activeSubOrg.value && !canModifySubOrg(activeSubOrg.value)) {
    return 'Selected suborganization is deleted. User and membership actions are read-only.'
  }

  return ''
})
const isContextReadOnly = computed(() => Boolean(contextReadOnlyMessage.value))
const assignableScopedSubOrgs = computed(() => scopedSubOrgs.value.filter((subOrg) => canModifySubOrg(subOrg)))

const refresh = async (force = false) => {
  const ready = await awaitScopedContextReady(authStore, orgStore)
  if (!ready) return { success: false, pendingContext: true }

  const contextKey = JSON.stringify({
    userId: authStore.user?.id ?? null,
    orgId: orgStore.activeOrgId ?? null,
    subOrgId: subOrgStore.currentSubOrgId ?? null,
  })

  if (!force && contextKey === lastLoadedContextKey.value) {
    return { success: true, cached: true }
  }

  lastLoadedContextKey.value = contextKey
  return usersStore.fetchUsers()
}

const resetSubOrgModalState = () => {
  subOrgModalOpen.value = false
  selectedUserForSubOrg.value = null
  subOrgModalLoading.value = false
  subOrgModalSaving.value = false
  subOrgModalError.value = ''
  selectedRole.value = ''
  originalRole.value = ''
  selectedSubOrgs.value = new Set()
  originalSubOrgs.value = new Set()
}

const clearOperationFeedback = () => {
  operationError.value = ''
  operationMessage.value = ''
}

const setDeletingUsers = (userIds, active) => {
  const next = new Set(deletingUserIds.value)
  userIds.forEach((id) => {
    const normalizedId = Number(id)
    if (!Number.isInteger(normalizedId) || normalizedId <= 0) return
    if (active) {
      next.add(normalizedId)
    } else {
      next.delete(normalizedId)
    }
  })
  deletingUserIds.value = next
}

const setUpdatingUser = (userId, active) => {
  const normalizedUserId = Number(userId)
  if (!Number.isInteger(normalizedUserId) || normalizedUserId <= 0) return

  const next = new Set(updatingUserIds.value)
  if (active) {
    next.add(normalizedUserId)
  } else {
    next.delete(normalizedUserId)
  }
  updatingUserIds.value = next
}

const pruneSelectedUsers = () => {
  const visibleIds = new Set(usersList.value.map((user) => Number(user?.id)).filter((id) => Number.isInteger(id) && id > 0))
  selectedUsers.value = new Set(Array.from(selectedUsers.value).filter((id) => visibleIds.has(Number(id))))
}

const getUserLabel = (user) => {
  if (!user) return 'Selected user'
  return user.username || user.email || `User #${user.id}`
}

const findUserById = (userId) => {
  return usersList.value.find((user) => Number(user?.id) === Number(userId)) ?? null
}

const getOrgName = (orgId) => {
  return orgStore.organizations.find((o) => Number(o.id) === Number(orgId))?.name || 'Unknown'
}

const getUserOrgEntity = (user) => {
  const orgId = Number(user?.organization_id)
  if (!Number.isInteger(orgId) || orgId <= 0) return null
  return orgStore.organizations.find((org) => Number(org?.id) === orgId) ?? null
}

const getUserDeletedMembershipCount = (user) => {
  const subOrgIds = Array.isArray(user?.sub_org_ids) ? user.sub_org_ids : []
  if (!subOrgIds.length) return 0

  const indexedSubOrgs = new Map((orgStore.subOrgs ?? []).map((subOrg) => [Number(subOrg?.id), subOrg]))
  return subOrgIds.reduce((count, subOrgId) => {
    const subOrg = indexedSubOrgs.get(Number(subOrgId))
    if (subOrg && !canModifySubOrg(subOrg)) {
      return count + 1
    }
    return count
  }, 0)
}

const isUserReadOnly = (user) => {
  if (!user) return true
  if (isContextReadOnly.value) return true

  const userOrg = getUserOrgEntity(user)
  if (userOrg && !canModifyOrg(userOrg)) return true

  return getUserDeletedMembershipCount(user) > 0
}

const getUserReadOnlyReason = (user) => {
  if (!user) return 'User is not available.'
  if (isContextReadOnly.value) return contextReadOnlyMessage.value

  const userOrg = getUserOrgEntity(user)
  if (userOrg && !canModifyOrg(userOrg)) {
    return 'User belongs to a deleted organization. Membership and privilege actions are read-only.'
  }

  if (getUserDeletedMembershipCount(user) > 0) {
    return 'User has deleted suborganization memberships. Membership and privilege actions are read-only.'
  }

  return ''
}

const roleFilterOptions = computed(() => {
  return Array.from(new Set(usersList.value.map((user) => user?.role).filter(Boolean))).sort()
})

const orgFilterOptions = computed(() => {
  const ids = new Set(usersList.value.map((user) => Number(user?.organization_id)).filter((id) => Number.isInteger(id) && id > 0))
  return Array.from(ids)
    .map((id) => ({ id, name: getOrgName(id) }))
    .sort((left, right) => String(left.name).localeCompare(String(right.name)))
})

const subOrgFilterOptions = computed(() => {
  const idSet = new Set()
  usersList.value.forEach((user) => {
    const ids = Array.isArray(user?.sub_org_ids) ? user.sub_org_ids : []
    ids.forEach((value) => {
      const normalizedId = Number(value)
      if (Number.isInteger(normalizedId) && normalizedId > 0) {
        idSet.add(normalizedId)
      }
    })
  })

  return Array.from(idSet)
    .map((id) => {
      const subOrg = (orgStore.subOrgs ?? []).find((item) => Number(item?.id) === Number(id))
      return {
        id,
        name: subOrg?.name ?? `SubOrg #${id}`,
      }
    })
    .sort((left, right) => String(left.name).localeCompare(String(right.name)))
})

const filteredUsers = computed(() => {
  const searchTerm = String(debouncedUserSearch.value || '').trim().toLowerCase()

  return usersList.value.filter((user) => {
    if (searchTerm) {
      const haystack = [user?.username, user?.email, user?.full_name].map((value) => String(value ?? '').toLowerCase())
      if (!haystack.some((value) => value.includes(searchTerm))) {
        return false
      }
    }

    if (selectedRoleFilter.value !== 'all' && String(user?.role) !== selectedRoleFilter.value) {
      return false
    }

    if (selectedOrgFilter.value !== 'all' && Number(user?.organization_id) !== Number(selectedOrgFilter.value)) {
      return false
    }

    if (selectedSubOrgFilter.value !== 'all') {
      const ids = Array.isArray(user?.sub_org_ids) ? user.sub_org_ids : []
      if (!ids.some((id) => Number(id) === Number(selectedSubOrgFilter.value))) {
        return false
      }
    }

    if (selectedLifecycleFilter.value !== 'all') {
      const readOnly = isUserReadOnly(user)
      if (selectedLifecycleFilter.value === 'active' && readOnly) return false
      if (selectedLifecycleFilter.value === 'deleted' && !readOnly) return false
    }

    return true
  })
})

const paginatedUsers = computed(() => paginateRows(filteredUsers.value, listPage.value, listPageSize.value))
const visibleUsers = computed(() => paginatedUsers.value.items)
const hasPreviousPage = computed(() => Number(listPage.value) > 1)
const hasNextPage = computed(() => Number(listPage.value) < Number(paginatedUsers.value.totalPages))

const queryState = useQueryState({
  route,
  router,
  entries: [
    { queryKey: 'q', defaultValue: '', get: () => userSearch.value, set: (value) => { userSearch.value = String(value || '') } },
    { queryKey: 'role', defaultValue: 'all', get: () => selectedRoleFilter.value, set: (value) => { selectedRoleFilter.value = String(value || 'all') } },
    { queryKey: 'org', defaultValue: 'all', get: () => selectedOrgFilter.value, set: (value) => { selectedOrgFilter.value = String(value || 'all') } },
    { queryKey: 'suborg', defaultValue: 'all', get: () => selectedSubOrgFilter.value, set: (value) => { selectedSubOrgFilter.value = String(value || 'all') } },
    { queryKey: 'lifecycle', defaultValue: 'all', get: () => selectedLifecycleFilter.value, set: (value) => { selectedLifecycleFilter.value = String(value || 'all') } },
    { queryKey: 'page', type: 'int', defaultValue: 1, get: () => listPage.value, set: (value) => { listPage.value = toPositiveInt(value, 1) } },
    { queryKey: 'page_size', type: 'int', defaultValue: DEFAULT_ADMIN_PAGE_SIZE, get: () => listPageSize.value, set: (value) => { listPageSize.value = toPositiveInt(value, DEFAULT_ADMIN_PAGE_SIZE) } },
  ],
})

const syncQueryFromState = async () => {
  await queryState.syncToRoute()
}

const hydrateStateFromQuery = () => {
  queryState.hydrateFromQuery()
}

const changePage = async (delta) => {
  const next = Number(listPage.value) + Number(delta)
  listPage.value = Math.max(1, Math.min(Number(paginatedUsers.value.totalPages), next))
  await syncQueryFromState()
}

const updatePageSize = async (nextPageSize) => {
  listPageSize.value = toPositiveInt(nextPageSize, DEFAULT_ADMIN_PAGE_SIZE)
  listPage.value = 1
  await syncQueryFromState()
}

const filteredUsersEmptyMessage = computed(() => {
  if (!usersList.value.length) return emptyUsersMessage.value
  return 'No users match the current filters.'
})

const hasMembershipChanges = () => {
  const original = Array.from(originalSubOrgs.value).sort((a, b) => a - b)
  const current = Array.from(selectedSubOrgs.value).sort((a, b) => a - b)

  if (original.length !== current.length) return true
  return original.some((value, index) => value !== current[index])
}

const openSubOrgModal = async (user) => {
  if (isUserReadOnly(user)) {
    operationError.value = getUserReadOnlyReason(user)
    return
  }

  clearOperationFeedback()
  selectedUserForSubOrg.value = user
  subOrgModalLoading.value = true
  subOrgModalSaving.value = false
  subOrgModalError.value = ''
  selectedRole.value = user?.role ?? ''
  originalRole.value = user?.role ?? ''
  if (!assignableRoles.value.includes(selectedRole.value)) {
    selectedRole.value = assignableRoles.value[0] ?? ''
  }
  selectedSubOrgs.value = new Set()
  originalSubOrgs.value = new Set()

  try {
    await subOrgStore.fetchSubOrgs({ status: 'all' })
    const result = await usersStore.loadUserSubOrgs(user.id, true)
    if (!result.success) {
      subOrgModalError.value = result.error ?? 'Failed to load user sub-organization memberships.'
    } else {
      const scopedIds = new Set(scopedSubOrgs.value.map((subOrg) => Number(subOrg?.id)).filter((id) => Number.isInteger(id)))
      const memberIds = usersStore
        .getUserSubOrgs(user.id)
        .map((item) => Number(item?.sub_org_id))
        .filter((id) => Number.isInteger(id) && scopedIds.has(id))
      const initial = new Set(memberIds)
      selectedSubOrgs.value = initial
      originalSubOrgs.value = new Set(initial)
    }
  } finally {
    subOrgModalLoading.value = false
    subOrgModalOpen.value = true
  }
}

const closeSubOrgModal = () => {
  resetSubOrgModalState()
}

const closePendingAction = () => {
  if (bulkDeleteLoading.value || subOrgModalSaving.value || pendingActionLoading.value) return
  pendingAction.value = null
}

const isCheckedForUser = (subOrgId) => {
  return selectedSubOrgs.value.has(Number(subOrgId))
}

const toggleSelectedSubOrg = (subOrgId) => {
  const subOrg = scopedSubOrgs.value.find((item) => Number(item?.id) === Number(subOrgId))
  if (!canModifySubOrg(subOrg)) {
    subOrgModalError.value = 'Cannot modify memberships for deleted suborganizations.'
    return
  }

  const next = new Set(selectedSubOrgs.value)
  const normalizedId = Number(subOrgId)
  if (next.has(normalizedId)) {
    next.delete(normalizedId)
  } else {
    next.add(normalizedId)
  }
  selectedSubOrgs.value = next
}

const performSaveSubOrgMemberships = async () => {
  const userId = Number(selectedUserForSubOrg.value?.id)
  if (!Number.isInteger(userId) || userId <= 0) {
    subOrgModalError.value = 'Invalid user selected.'
    return
  }

  if (isUserReadOnly(selectedUserForSubOrg.value)) {
    subOrgModalError.value = getUserReadOnlyReason(selectedUserForSubOrg.value)
    return
  }

  if (getUserDeletedMembershipCount(selectedUserForSubOrg.value) > 0) {
    subOrgModalError.value = 'Cannot change role while deleted memberships exist for this user.'
    return
  }

  subOrgModalSaving.value = true
  setUpdatingUser(userId, true)
  subOrgModalError.value = ''
  clearOperationFeedback()
  try {
    const allowedSubOrgIds = new Set(
      scopedSubOrgs.value
        .map((subOrg) => Number(subOrg?.id))
        .filter((id) => Number.isInteger(id) && id > 0),
    )
    const original = new Set(originalSubOrgs.value)
    const current = new Set(selectedSubOrgs.value)

    const toAdd = Array.from(current).filter((subOrgId) => {
      if (!allowedSubOrgIds.has(Number(subOrgId))) {
        console.warn('[SUBORG ASSIGNMENT] Ignoring subOrg outside active organization', {
          userId,
          subOrgId,
          activeOrgId: orgStore.activeOrgId,
        })
        return false
      }
      return !original.has(subOrgId)
    })
    const toRemove = Array.from(original).filter((subOrgId) => {
      if (!allowedSubOrgIds.has(Number(subOrgId))) {
        console.warn('[SUBORG ASSIGNMENT] Ignoring removal for subOrg outside active organization', {
          userId,
          subOrgId,
          activeOrgId: orgStore.activeOrgId,
        })
        return false
      }
      return !current.has(subOrgId)
    })

    for (const subOrgId of toAdd) {
      await addUserSubOrgRequest(userId, subOrgId)
    }

    for (const subOrgId of toRemove) {
      await removeUserSubOrgRequest(userId, subOrgId)
    }

    await Promise.all([
      usersStore.loadUserSubOrgs(selectedUserForSubOrg.value.id, true),
      usersStore.fetchUsers(),
    ])

    if (
      assignableRoles.value.length > 0
      && assignableRoles.value.includes(selectedRole.value)
      && selectedRole.value !== originalRole.value
    ) {
      const roleResult = await usersStore.updateUserRole(userId, selectedRole.value)
      if (!roleResult.success) {
        subOrgModalError.value = parseApiError(roleResult.error, 'Failed to update user role.')
        return
      }
    }

    operationMessage.value = 'User memberships and privileges updated successfully.'
    closeSubOrgModal()
  } catch (err) {
    const message = parseApiError(err, 'Failed to save user access changes.')
    const status = Number(err?.response?.status || 0)

    if (status === 404 || status === 403) {
      await refresh(true)
      operationError.value = message
      closeSubOrgModal()
      return
    }

    subOrgModalError.value = message
  } finally {
    subOrgModalSaving.value = false
    setUpdatingUser(userId, false)
  }
}

const requestSaveSubOrgMemberships = async () => {
  const user = selectedUserForSubOrg.value
  if (!user?.id) {
    subOrgModalError.value = 'Invalid user selected.'
    return
  }

  if (subOrgModalSaving.value || pendingActionLoading.value) return

  if (isUserReadOnly(user)) {
    subOrgModalError.value = getUserReadOnlyReason(user)
    return
  }

  subOrgModalError.value = ''

  const removedMembershipCount = Array.from(originalSubOrgs.value).filter((subOrgId) => !selectedSubOrgs.value.has(subOrgId)).length
  const roleChanged = hasPendingRoleChange.value
  const roleEscalation = roleChanged && ['admin', 'manager'].includes(selectedRole.value)

  if (!roleChanged && removedMembershipCount === 0) {
    await performSaveSubOrgMemberships()
    return
  }

  pendingAction.value = {
    type: roleEscalation ? 'privilege-change' : roleChanged ? 'role-change' : 'membership-removal',
    userId: Number(user.id),
    userLabel: getUserLabel(user),
    previousRole: originalRole.value || 'current role',
    nextRole: selectedRole.value,
    includesMembershipChanges: hasMembershipChanges(),
    removedMembershipCount,
  }
}



const parseApiError = (err, fallback) => {
  return parseAdminError(err, fallback, {
    notFound: 'This user is no longer available. Refresh the list and try again.',
    forbidden: 'You no longer have permission to manage this user.',
    conflict: 'This user could not be updated because their access changed. Refresh and try again.',
  })
}

const toggleUserSelection = (userId) => {
  const user = findUserById(userId)
  if (isUserReadOnly(user)) {
    operationError.value = getUserReadOnlyReason(user)
    return
  }

  const next = new Set(selectedUsers.value)
  if (next.has(userId)) {
    next.delete(userId)
  } else {
    next.add(userId)
  }
  selectedUsers.value = next
}

const toggleSelectAll = (users) => {
  const selectableUsers = users.filter((user) => !isUserReadOnly(user))
  if (!selectableUsers.length) {
    operationError.value = 'No active users available for bulk operations in the current lifecycle state.'
    return
  }

  if (selectedUsers.value.size === selectableUsers.length) {
    selectedUsers.value = new Set()
  } else {
    selectedUsers.value = new Set(selectableUsers.map((user) => user.id))
  }
}

const getReadOnlySelectedUsers = (userIds) => {
  return userIds
    .map((id) => findUserById(id))
    .filter((user) => user && isUserReadOnly(user))
}

const bulkAddToSubOrg = async () => {
  clearOperationFeedback()

  if (isContextReadOnly.value) {
    operationError.value = contextReadOnlyMessage.value
    return
  }

  const subOrgId = Number(selectedBulkSubOrgId.value)
  if (!Number.isInteger(subOrgId) || subOrgId <= 0) {
    operationError.value = 'Please select a sub-organization.'
    return
  }

  const targetSubOrg = scopedSubOrgs.value.find((subOrg) => Number(subOrg?.id) === subOrgId)
  if (!canModifySubOrg(targetSubOrg)) {
    operationError.value = 'Cannot assign users to a deleted suborganization.'
    return
  }

  const userIds = Array.from(selectedUsers.value)
  if (!userIds.length) {
    operationError.value = 'Please select at least one user.'
    return
  }

  const readOnlyUsers = getReadOnlySelectedUsers(userIds)
  if (readOnlyUsers.length) {
    operationError.value = 'One or more selected users are in read-only lifecycle state and cannot be reassigned.'
    return
  }

  assignLoading.value = true
  try {
    await Promise.all(
      userIds.map((id) => addUserSubOrgRequest(Number(id), subOrgId)),
    )

    await Promise.all(
      userIds.map((id) => usersStore.loadUserSubOrgs(id, true)),
    )

    await usersStore.fetchUsers()

    operationMessage.value = 'Users added to the sub-organization successfully.'
    selectedUsers.value = new Set()
  } catch (err) {
    console.error('Failed to add users to sub-organization', err)
    operationError.value = parseApiError(err, 'Failed to add users to sub-organization.')
  } finally {
    assignLoading.value = false
  }
}

const bulkRemoveFromSubOrg = async () => {
  clearOperationFeedback()

  if (isContextReadOnly.value) {
    operationError.value = contextReadOnlyMessage.value
    return
  }

  const subOrgId = Number(selectedBulkSubOrgId.value)
  if (!Number.isInteger(subOrgId) || subOrgId <= 0) {
    operationError.value = 'Please select a sub-organization.'
    return
  }

  const targetSubOrg = scopedSubOrgs.value.find((subOrg) => Number(subOrg?.id) === subOrgId)
  if (!canModifySubOrg(targetSubOrg)) {
    operationError.value = 'Cannot remove users from a deleted suborganization.'
    return
  }

  const userIds = Array.from(selectedUsers.value)
  if (!userIds.length) {
    operationError.value = 'Please select at least one user.'
    return
  }

  const readOnlyUsers = getReadOnlySelectedUsers(userIds)
  if (readOnlyUsers.length) {
    operationError.value = 'One or more selected users are in read-only lifecycle state and cannot be modified.'
    return
  }

  assignLoading.value = true
  try {
    await Promise.all(
      userIds.map((id) => removeUserSubOrgRequest(Number(id), subOrgId)),
    )

    await Promise.all(
      userIds.map((id) => usersStore.loadUserSubOrgs(id, true)),
    )

    await usersStore.fetchUsers()

    operationMessage.value = 'Users removed from the sub-organization successfully.'
    selectedUsers.value = new Set()
  } catch (err) {
    console.error('Failed to remove users from sub-organization', err)
    operationError.value = parseApiError(err, 'Failed to remove users from sub-organization.')
  } finally {
    assignLoading.value = false
  }
}

const deleteUsers = async (userIds, { bulk = false } = {}) => {
  const normalizedUserIds = Array.from(new Set(userIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)))
  if (!normalizedUserIds.length) {
    operationError.value = 'Please select at least one user.'
    return { success: false }
  }

  if (isContextReadOnly.value) {
    operationError.value = contextReadOnlyMessage.value
    return { success: false }
  }

  const readOnlyUsers = getReadOnlySelectedUsers(normalizedUserIds)
  if (readOnlyUsers.length) {
    operationError.value = 'Cannot delete users that are tied to deleted organization or suborganization memberships.'
    return { success: false }
  }

  clearOperationFeedback()
  setDeletingUsers(normalizedUserIds, true)

  if (bulk) {
    bulkDeleteLoading.value = true
  }

  let deletedCount = 0
  let firstError = ''

  try {
    for (const userId of normalizedUserIds) {
      const result = await usersStore.deleteUser(userId)
      if (result.success) {
        deletedCount += 1
        continue
      }

      if (!firstError) {
        firstError = parseApiError(result.error, 'Failed to delete user.')
      }
    }

    await refresh(true)
    pruneSelectedUsers()

    if (selectedUserForSubOrg.value && !findUserById(selectedUserForSubOrg.value.id)) {
      closeSubOrgModal()
    }

    if (firstError) {
      operationError.value = deletedCount > 0
        ? `${deletedCount} user(s) deleted. ${firstError}`
        : firstError
      return { success: false, deletedCount }
    }

    operationMessage.value = deletedCount === 1
      ? 'User deleted successfully.'
      : `${deletedCount} users deleted successfully.`

    return { success: true, deletedCount }
  } finally {
    setDeletingUsers(normalizedUserIds, false)
    if (bulk) {
      bulkDeleteLoading.value = false
    }
  }
}

const requestBulkDeleteUsers = () => {
  clearOperationFeedback()

  const userIds = Array.from(selectedUsers.value)
  if (!userIds.length) {
    operationError.value = 'Please select at least one user.'
    return
  }

  pendingAction.value = {
    type: 'bulk-delete-users',
    userIds,
    count: userIds.length,
  }
}

const requestDeleteSingleUser = (id) => {
  clearOperationFeedback()

  const user = findUserById(id)
  if (isUserReadOnly(user)) {
    operationError.value = getUserReadOnlyReason(user)
    return
  }

  pendingAction.value = {
    type: 'delete-user',
    userIds: [Number(id)],
    userLabel: getUserLabel(user ?? { id }),
  }
}

const confirmPendingAction = async () => {
  const action = pendingAction.value
  if (!action || pendingActionLoading.value) return

  pendingActionLoading.value = true

  try {
    if (action.type === 'delete-user') {
      await deleteUsers(action.userIds, { bulk: false })
    }

    if (action.type === 'bulk-delete-users') {
      const result = await deleteUsers(action.userIds, { bulk: true })
      if (result?.success) {
        selectedUsers.value = new Set()
      }
    }

    if (action.type === 'role-change' || action.type === 'privilege-change' || action.type === 'membership-removal') {
      await performSaveSubOrgMemberships()
    }
  } finally {
    pendingActionLoading.value = false
    pendingAction.value = null
  }
}

onMounted(async () => {
  hydrateStateFromQuery()
  await refresh()
  await syncQueryFromState()
})

watch([debouncedUserSearch, selectedRoleFilter, selectedOrgFilter, selectedSubOrgFilter, selectedLifecycleFilter], async () => {
  listPage.value = 1
  await syncQueryFromState()
})

watch(
  () => orgStore.activeOrgId,
  (nextOrgId, previousOrgId) => {
    if (nextOrgId === previousOrgId) return
    resetSubOrgModalState()
    clearOperationFeedback()
    selectedBulkSubOrgId.value = ''
  },
)

watch(
  () => orgStore.activeSubOrgId,
  (nextSubOrgId, previousSubOrgId) => {
    if (nextSubOrgId === previousSubOrgId) return
    selectedBulkSubOrgId.value = ''
  },
)

watch(usersList, () => {
  pruneSelectedUsers()

  if (selectedUserForSubOrg.value && !findUserById(selectedUserForSubOrg.value.id)) {
    closeSubOrgModal()
    operationError.value = 'The selected user is no longer available. The list has been refreshed.'
  }
})

watch(
  [() => authStore.contextReady, () => authStore.user?.id, () => orgStore.activeOrgId, () => subOrgStore.currentSubOrgId],
  async ([ready, nextUserId, nextOrgId, nextSubOrgId], [previousReady, previousUserId, previousOrgId, previousSubOrgId]) => {
    if (!ready) {
      lastLoadedContextKey.value = ''
      return
    }

    if (
      ready === previousReady
      && nextUserId === previousUserId
      && nextOrgId === previousOrgId
      && nextSubOrgId === previousSubOrgId
    ) {
      return
    }

    await refresh()
  },
)

watch(
  () => route.fullPath,
  () => {
    pendingAction.value = null
    pendingActionLoading.value = false
    assignLoading.value = false
    bulkDeleteLoading.value = false
    deletingUserIds.value = new Set()
    updatingUserIds.value = new Set()
  },
)
</script>

<template>
  <div class="users-view">
    <h1>User Administration</h1>
    <CreateUserForm @created="refresh(true)" />

    <UsersLifecycleWarnings
      :users-error="usersStore.error"
      :operation-message="operationMessage"
      :operation-error="operationError"
      :context-read-only-message="contextReadOnlyMessage"
    />

    <!-- Delegated filter controls keep contract selectors: data-testid="users-search", data-testid="users-lifecycle-filter", data-testid="users-page-size" -->
    <UsersFilterBar
      :user-search="userSearch"
      :selected-role-filter="selectedRoleFilter"
      :selected-org-filter="selectedOrgFilter"
      :selected-sub-org-filter="selectedSubOrgFilter"
      :selected-lifecycle-filter="selectedLifecycleFilter"
      :list-page-size="listPageSize"
      :role-filter-options="roleFilterOptions"
      :org-filter-options="orgFilterOptions"
      :sub-org-filter-options="subOrgFilterOptions"
      @update:user-search="userSearch = $event"
      @update:selected-role-filter="selectedRoleFilter = $event"
      @update:selected-org-filter="selectedOrgFilter = $event"
      @update:selected-sub-org-filter="selectedSubOrgFilter = $event"
      @update:selected-lifecycle-filter="selectedLifecycleFilter = $event"
      @update:list-page-size="updatePageSize($event)"
    />

    <UsersSummaryBar :filtered-count="filteredUsers.length" :page="listPage" :total-pages="paginatedUsers.totalPages" />

    <div class="panel actions-bar sticky-action-bar">
      <label class="form-label">
        <select v-model="selectedBulkSubOrgId" class="form-select" :disabled="bulkActionLocked">
          <option value="">-- Select SubOrg: --</option>
          <option v-for="s in assignableScopedSubOrgs" :key="s.id" :value="String(s.id)">
            {{ s.name }}
          </option>
        </select>
      </label>

      <button class="btn btn-primary" :disabled="bulkActionLocked || !hasSelectedUsers || isContextReadOnly" @click="bulkAddToSubOrg">
        {{ assignLoading ? 'Updating...' : 'Add to SubOrg' }}
      </button>

      <button class="btn btn-secondary" :disabled="bulkActionLocked || !hasSelectedUsers || isContextReadOnly" @click="bulkRemoveFromSubOrg">
        {{ assignLoading ? 'Updating...' : 'Remove from SubOrg' }}
      </button>

      <button class="btn btn-danger" :disabled="bulkActionLocked || !hasSelectedUsers || isContextReadOnly" @click="requestBulkDeleteUsers">
        {{ bulkDeleteLoading ? 'Deleting...' : 'Delete Users' }}
      </button>
    </div>

    <div v-if="usersStore.loading && !usersList.length" class="skeleton-grid">
      <UserSkeletonCard v-for="n in 4" :key="n" />
    </div>

    <UserTable
      v-else
      :users="visibleUsers"
      :selected-users="selectedUsers"
      :get-org-name="getOrgName"
      :highlight-sub-org-id="highlightSubOrgId"
      :has-sub-org-access="hasSubOrgAccess"
      :on-toggle-user-selection="toggleUserSelection"
      :on-toggle-select-all="toggleSelectAll"
      :on-manage-sub-orgs="openSubOrgModal"
      :on-delete-user="requestDeleteSingleUser"
      :deleting-user-ids="deletingUserIds"
      :actions-disabled="bulkActionLocked"
      :empty-message="filteredUsersEmptyMessage"
      :updating-user-ids="updatingUserIds"
      :is-user-read-only="isUserReadOnly"
      :get-user-read-only-reason="getUserReadOnlyReason"
      :get-user-org="getUserOrgEntity"
      :get-user-deleted-membership-count="getUserDeletedMembershipCount"
    />

    <footer class="pagination-row" v-if="filteredUsers.length">
      <button class="btn btn-secondary" type="button" :disabled="usersStore.loading || !hasPreviousPage" @click="changePage(-1)">Previous</button>
      <span>Page {{ listPage }} of {{ paginatedUsers.totalPages }} ({{ filteredUsers.length }} users)</span>
      <button class="btn btn-secondary" type="button" :disabled="usersStore.loading || !hasNextPage" @click="changePage(1)">Next</button>
    </footer>

    <div v-if="subOrgModalOpen" class="modal-backdrop" @click.self="closeSubOrgModal">
      <div class="modal-panel">
        <h3>Manage User: {{ selectedUserForSubOrg?.username || selectedUserForSubOrg?.email || `User #${selectedUserForSubOrg?.id}` }}</h3>
        <p v-if="subOrgModalLoading">Loading sub-organizations...</p>
        <p v-else-if="subOrgModalSaving">Saving changes...</p>
        <p v-if="subOrgModalError" class="error">{{ subOrgModalError }}</p>

        <div v-if="!subOrgModalLoading" class="modal-sections">
          <div class="panel">
            <h4>Role</h4>
            <p v-if="!assignableRoles.length">You do not have permission to assign roles</p>
            <p v-if="getUserDeletedMembershipCount(selectedUserForSubOrg) > 0" class="warning">
              Role changes are disabled while deleted memberships exist.
            </p>
            <select v-model="selectedRole" class="form-select" :disabled="roleAssignmentDisabled">
              <option value="">-- Select Role --</option>
              <option v-for="role in assignableRoles" :key="role" :value="role">
                {{ role }}
              </option>
            </select>
          </div>

          <div class="panel users-list">
            <label v-for="subOrg in scopedSubOrgs" :key="subOrg.id" class="user-row" :class="{ 'read-only-row': !canModifySubOrg(subOrg) }">
              <input
                type="checkbox"
                :checked="isCheckedForUser(subOrg.id)"
                :disabled="subOrgModalSaving || !canModifySubOrg(subOrg)"
                @change="toggleSelectedSubOrg(subOrg.id)"
              />
              <span>{{ subOrg.name }} <span v-if="!canModifySubOrg(subOrg)">(deleted)</span></span>
            </label>
            <p v-if="!scopedSubOrgs.length">No sub-organizations available in this organization</p>
          </div>
        </div>

        <div class="actions-bar">
          <button class="btn btn-primary" type="button" :disabled="subOrgModalLoading || subOrgModalSaving" @click="requestSaveSubOrgMemberships">
            {{ subOrgModalSaving ? 'Saving...' : 'Save' }}
          </button>
          <button class="btn btn-secondary" type="button" :disabled="subOrgModalSaving" @click="closeSubOrgModal">Close</button>
        </div>
      </div>
    </div>

    <AdminActionDialog
      :open="Boolean(pendingAction)"
      :title="pendingAction?.type === 'role-change'
        ? 'Confirm Role Change'
        : pendingAction?.type === 'privilege-change'
          ? 'Confirm Privilege Change'
          : pendingAction?.type === 'membership-removal'
            ? 'Confirm Membership Removal'
        : pendingAction?.type === 'bulk-delete-users'
          ? 'Deactivate Selected Users'
          : 'Deactivate User'"
      :message="pendingAction?.type === 'role-change'
        ? `${pendingAction?.userLabel} will change from ${pendingAction?.previousRole} to ${pendingAction?.nextRole}.`
        : pendingAction?.type === 'privilege-change'
          ? `${pendingAction?.userLabel} will receive higher privileges (${pendingAction?.previousRole} to ${pendingAction?.nextRole}).`
          : pendingAction?.type === 'membership-removal'
            ? `${pendingAction?.removedMembershipCount} membership(s) will be removed for ${pendingAction?.userLabel}.`
        : pendingAction?.type === 'bulk-delete-users'
          ? `This will deactivate ${pendingAction?.count} selected user accounts.`
          : `${pendingAction?.userLabel ?? 'This user'} will be deactivated.`"
      :details="pendingAction?.type === 'role-change'
        ? [
            'Role changes take effect the next time permissions are evaluated.',
            ...(pendingAction?.includesMembershipChanges ? ['Sub-organization membership changes will also be saved.'] : []),
          ]
        : pendingAction?.type === 'privilege-change'
          ? ['Privilege changes are audit-logged and affect access immediately after policy evaluation.']
          : pendingAction?.type === 'membership-removal'
            ? ['Removed memberships can be re-added later from user access management.']
            : ['Deactivated users keep audit history and can be managed again if backend permits.']"
      :confirm-label="pendingAction?.type === 'role-change'
        ? 'Confirm Role Change'
        : pendingAction?.type === 'privilege-change'
          ? 'Confirm Privilege Change'
          : pendingAction?.type === 'membership-removal'
            ? 'Confirm Membership Removal'
        : pendingAction?.type === 'bulk-delete-users'
          ? 'Deactivate Users'
          : 'Deactivate User'"
      :loading-label="pendingAction?.type === 'role-change' || pendingAction?.type === 'privilege-change' || pendingAction?.type === 'membership-removal'
        ? 'Saving user changes...'
        : 'Deactivating user... '"
      :tone="pendingAction?.type === 'role-change' || pendingAction?.type === 'privilege-change' ? 'warning' : pendingAction?.type === 'membership-removal' ? 'neutral' : 'danger'"
      :loading="pendingActionLoading || subOrgModalSaving || bulkDeleteLoading"
      @close="closePendingAction"
      @confirm="confirmPendingAction"
    />
  </div>
</template>

<style scoped>
.users-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  gap: 1rem;
}

h1 {
  color: #111827;
}

.users-list {
  display: grid;
  gap: 0.35rem;
  max-height: 220px;
  overflow: auto;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem;
  background: #fff;
}

.user-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error {
  color: #b91c1c;
  background: #fee2e2;
  border-radius: 6px;
  padding: 0.45rem;
}

.success {
  color: #166534;
  background: #dcfce7;
  border-radius: 6px;
  padding: 0.45rem;
}

.warning {
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 0.45rem;
}

.filters-bar {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.filters-bar input,
.filters-bar select {
  min-width: 0;
}

.read-only-row {
  opacity: 0.75;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  display: grid;
  place-items: center;
  z-index: 1000;
}

.modal-panel {
  width: min(560px, 92vw);
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
}

.modal-sections {
  display: grid;
  gap: 0.75rem;
}

.skeleton-grid {
  display: grid;
  gap: 0.75rem;
}

.sticky-action-bar {
  position: sticky;
  top: 56px;
  background: #fff;
  z-index: 10;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.pagination-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

@media (max-height: 500px) {

  .sticky-action-bar {
    position: fixed;
    top: 56px;
    left: 0;

    width: 220px;
    height: calc(100vh - 56px);

    overflow-y: auto;

    background: #fff;
    border-right: 1px solid #e5e7eb;

    padding: 1rem;

    z-index: 20;
  }

}

@media (max-width: 768px) {
  .sticky-action-bar {
    position: static;
    top: auto;
    border-bottom: none;
    padding: 0;
    display: grid;
    gap: 0.5rem;
  }

  .sticky-action-bar .btn,
  .sticky-action-bar .form-label,
  .sticky-action-bar .form-select {
    width: 100%;
  }
}

@media (max-height: 500px) {

  .users-view {
    padding-left: 240px;
  }

}
</style>
